import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bus, Check, GraduationCap, MapPin, RefreshCw, Send } from "lucide-react";
import { api, formatError } from "@/lib/api";

const todaySyd = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });

const PickupCard = ({ p, onToggle }) => (
  <motion.button
    layout
    whileTap={{ scale: 0.97 }}
    data-testid={`pickup-${p.enrolment_id}`}
    onClick={() => onToggle(p)}
    className={`flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-colors duration-300 ${p.picked_up ? "bg-gum text-sand" : "bg-white/70 text-ink"}`}
  >
    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 ${p.picked_up ? "border-sand bg-sand text-gum" : "border-ink/30"}`}>
      {p.picked_up && <Check className="h-6 w-6" strokeWidth={3} />}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1 text-lg font-semibold"><MapPin className="h-4 w-4 shrink-0 text-ochre" />{p.pickup_suburb}</span>
      <span className="block text-base">{p.name} <span className="text-sm opacity-75">· {p.transport_for}</span></span>
      {p.transport_notes && <span className="mt-1 block text-sm opacity-85">{p.transport_notes}</span>}
      {p.picked_up && p.picked_up_at && <span className="mt-1 block font-mono text-xs uppercase opacity-80">Picked up {new Date(p.picked_up_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}</span>}
    </span>
  </motion.button>
);

export default function Driver() {
  const [date, setDate] = useState(todaySyd());
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/driver/today", { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ sessions: [] }));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (session, p) => {
    const next = !p.picked_up;
    const upd = (l) => (l.enrolment_id === p.enrolment_id ? { ...l, picked_up: next, picked_up_at: next ? new Date().toISOString() : null } : l);
    setData((d) => ({ ...d, school_runs: session.id === "school" ? (d.school_runs || []).map(upd) : d.school_runs, sessions: d.sessions.map((s) => (s.id !== session.id ? s : { ...s, lifts: s.lifts.map(upd) })) }));
    try { await api.post("/driver/pickups", { date, session_id: session.id, enrolment_id: p.enrolment_id, picked_up: next }); }
    catch (e) { toast.error(formatError(e)); load(); }
  };

  const notify = async (s) => {
    try {
      const { data: r } = await api.post("/driver/all-picked-up", { date, session_id: s.id });
      setData((d) => ({ ...d, sessions: d.sessions.map((x) => (x.id === s.id ? { ...x, notified_at: r?.notified_at } : x)) }));
      toast.success("Staff have been told everyone is on the way");
    } catch (e) { toast.error(formatError(e)); }
  };

  const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
  const school = Array.isArray(data?.school_runs) ? data.school_runs : [];
  const all = [...school, ...sessions.flatMap((s) => s.lifts)];
  const done = all.filter((l) => l.picked_up).length;

  return (
    <main className="min-h-screen bg-sand px-4 pb-28 pt-28">
      <div className="mx-auto max-w-lg">
        <p className="eyebrow flex items-center gap-2 text-ochre"><Bus className="h-4 w-4" /> Driver run</p>
        <div className="mt-3 flex items-center justify-between gap-3">
          <input type="date" data-testid="driver-date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl border border-ink/20 bg-white/70 px-3 py-2 text-base" />
          <button data-testid="driver-refresh" onClick={load} className="rounded-full border border-ink/20 p-3" aria-label="Refresh"><RefreshCw className="h-5 w-5" /></button>
        </div>
        {all.length > 0 && (
          <div className="mt-6" data-testid="driver-progress">
            <p className="font-display text-4xl">{done} of {all.length} picked up</p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-ink/10"><motion.div className="h-full bg-gum" animate={{ width: `${(done / all.length) * 100}%` }} /></div>
          </div>
        )}
        {data && !all.length && <p data-testid="driver-empty" className="mt-10 rounded-2xl border border-dashed border-ink/25 p-8 text-center text-ink/75">No pick-ups for this day.</p>}
        <div className="mt-8 space-y-10">
          {school.length > 0 && (
            <section data-testid="driver-school-runs">
              <h2 className="flex items-center gap-2 font-display text-2xl leading-tight"><GraduationCap className="h-5 w-5 text-ochre" /> School runs</h2>
              <div className="mt-4 space-y-3">{school.map((p) => <PickupCard key={p.enrolment_id} p={p} onToggle={(x) => toggle({ id: "school" }, x)} />)}</div>
            </section>
          )}
          {sessions.map((s) => (
            <section key={s.id} data-testid={`driver-session-${s.id}`}>
              <h2 className="font-display text-2xl leading-tight">{s.course_title}</h2>
              <p className="mt-1 text-sm text-ink/75">Arrive by {s.start_time}{s.location ? ` · ${s.location}` : ""}</p>
              <div className="mt-4 space-y-3">{s.lifts.map((p) => <PickupCard key={p.enrolment_id} p={p} onToggle={(x) => toggle(s, x)} />)}</div>
              {s.lifts.every((l) => l.picked_up) && (
                <motion.button
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid={`all-picked-up-${s.id}`}
                  onClick={() => notify(s)}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ochre py-4 text-base font-semibold text-sand"
                >
                  <Send className="h-5 w-5" /> {s.notified_at ? "Notify staff again" : "All picked up — tell staff"}
                </motion.button>
              )}
              {s.notified_at && <p data-testid={`notified-${s.id}`} className="mt-2 text-center text-sm text-gum">Staff notified at {new Date(s.notified_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}</p>}
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
